/**
 * Formatting helpers for the three markets ClientMa serves.
 *
 * India is the reason this file exists: `en-IN` groups by lakh and crore
 * (12,34,567 rather than 1,234,567), and getting that wrong makes every figure on the screen
 * subtly wrong to an Indian reader. Everything routes through Intl so the browser's own data
 * does the work rather than a hand-rolled table.
 */

export type SupportedLocale = 'en-IN' | 'en-SG' | 'en-MY' | 'en-GB' | string;

/** Formats money in the given currency, in the viewer's locale. */
export function formatMoney(
  amount: number | null | undefined,
  currency = 'SGD',
  locale: SupportedLocale = 'en-SG',
  options: { compact?: boolean; decimals?: number } = {},
): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';

  const decimals = options.decimals ?? 2;

  // `Intl` throws a RangeError on a currency code it does not recognise — including an empty
  // one — and an uncaught throw inside a table cell takes the whole screen down with it. A
  // report spanning several currencies legitimately has no single code to use, so an absent or
  // unrecognised one renders the amount plainly rather than failing.
  const style: Intl.NumberFormatOptions =
    isUsableCurrency(currency)
      ? { style: 'currency', currency }
      : { style: 'decimal' };

  try {
    // Compact notation on a dashboard tile: 1.2M reads faster than 1,200,000 and the exact
    // figure is one click away.
    if (options.compact && Math.abs(amount) >= 1000) {
      return new Intl.NumberFormat(locale, {
        ...style,
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(amount);
    }

    return new Intl.NumberFormat(locale, {
      ...style,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount);
  } catch {
    // A locale the browser rejects as well. The number itself is still worth showing.
    return amount.toFixed(decimals);
  }
}

/**
 * Whether this is a currency code `Intl` will accept: exactly three letters (ISO 4217).
 *
 * Checked rather than caught, because a throw inside a render is expensive and this is the
 * common case — a record whose currency was never set.
 */
function isUsableCurrency(currency: string | null | undefined): currency is string {
  return typeof currency === 'string' && /^[A-Za-z]{3}$/.test(currency);
}

export function formatNumber(
  value: number | null | undefined,
  locale: SupportedLocale = 'en-SG',
  decimals = 0,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatPercent(
  value: number | null | undefined,
  locale: SupportedLocale = 'en-SG',
  decimals = 1,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';

  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)}%`;
}

/** Date only — the default across the three markets is dd/MM/yyyy. */
export function formatDate(
  value: string | Date | null | undefined,
  locale: SupportedLocale = 'en-SG',
  style: 'short' | 'medium' | 'long' = 'medium',
  timeZone?: string,
): string {
  const date = toDate(value);
  if (!date) return '—';

  const options: Intl.DateTimeFormatOptions =
    style === 'short'
      ? { day: '2-digit', month: '2-digit', year: 'numeric' }
      : style === 'long'
        ? { day: 'numeric', month: 'long', year: 'numeric' }
        : { day: 'numeric', month: 'short', year: 'numeric' };

  // The zone matters here as much as it does for a time: an instant late in the UTC day is
  // already tomorrow in Singapore, so a date rendered in the wrong zone is off by a whole day.
  //
  // Unless the value is a calendar date rather than an instant. A quote date, a due date and an
  // invoice date arrive as "2026-08-16" and mean that day everywhere — converting one into the
  // reader's zone is what dates an invoice the 15th for somebody in the Americas. Detected here
  // rather than left to each of the several hundred call sites to remember.
  return format(date, locale, options, timeZone ?? (isCalendarDate(value) ? 'UTC' : undefined));
}

/** True for "2026-08-16" — a day, with no time and so no instant to convert. */
function isCalendarDate(value: string | Date | null | undefined): boolean {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
}

/**
 * A calendar date that is not an instant, stated explicitly.
 *
 * `formatDate` already detects a bare "2026-08-16" and holds it in UTC. This is for the case
 * where a full timestamp is nonetheless meant as a day — a period end stored at midnight, say —
 * and must not move for the reader.
 */
export function formatCalendarDate(
  value: string | Date | null | undefined,
  locale: SupportedLocale = 'en-SG',
  style: 'short' | 'medium' | 'long' = 'medium',
): string {
  return formatDate(value, locale, style, 'UTC');
}

export function formatDateTime(
  value: string | Date | null | undefined,
  locale: SupportedLocale = 'en-SG',
  timeZone?: string,
): string {
  const date = toDate(value);
  if (!date) return '—';

  return format(date, locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }, timeZone);
}

export function formatTime(
  value: string | Date | null | undefined,
  locale: SupportedLocale = 'en-SG',
  timeZone?: string,
): string {
  const date = toDate(value);
  if (!date) return '—';

  return format(date, locale, { hour: '2-digit', minute: '2-digit' }, timeZone);
}

/**
 * Formats in the given zone, falling back to the viewer's own if the browser rejects it.
 *
 * `Intl` accepts IANA names only, and throws a RangeError on anything else — including the
 * Windows zone ids the server stores. A wrong-by-an-hour timestamp is a far smaller problem
 * than an exception that takes the whole screen down, which is what this used to do.
 */
function format(
  date: Date,
  locale: SupportedLocale,
  options: Intl.DateTimeFormatOptions,
  timeZone?: string,
): string {
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat(locale, options).format(date);
  }
}

/**
 * Relative time — "in 3 days", "2 hours ago".
 *
 * Uses Intl.RelativeTimeFormat so the phrasing is the browser's, which handles the awkward
 * cases (yesterday, last week) better than a hand-written ladder would.
 */
export function formatRelative(
  value: string | Date | null | undefined,
  locale: SupportedLocale = 'en-SG',
  now: Date = new Date(),
): string {
  const date = toDate(value);
  if (!date) return '—';

  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const seconds = (date.getTime() - now.getTime()) / 1000;

  const divisions: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.34524, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ];

  let duration = seconds;

  for (const [amount, unit] of divisions) {
    if (Math.abs(duration) < amount) {
      return formatter.format(Math.round(duration), unit);
    }

    duration /= amount;
  }

  return formatter.format(Math.round(duration), 'year');
}

/** Compact duration for a calendar chip: "1h 30m". */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return '—';

  const rounded = Math.round(minutes);

  if (rounded < 60) return `${rounded}m`;

  const hours = Math.floor(rounded / 60);
  const remainder = rounded % 60;

  return remainder === 0 ? `${hours}h` : `${hours}h ${remainder}m`;
}

/** Initials for an avatar. Two letters at most, so the circle stays legible. */
export function initialsOf(name: string | null | undefined): string {
  if (!name) return '?';

  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();

  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

/**
 * Parses a value from the API into an instant.
 *
 * A date-time string with no zone designator is treated as UTC, not local. ECMAScript specifies
 * the opposite — `new Date('2026-08-16T01:59:29')` is local time — and every timestamp this
 * product stores is UTC. Without this, a reader in Singapore saw every time eight hours out and
 * often on the wrong day, silently, because nothing in the value said which zone it was in.
 *
 * The server now sends the designator, so this is the second line of defence rather than the
 * first: it also covers anything cached from before that change, and any date a caller builds
 * by hand.
 */
function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const trimmed = value.trim();

  // A date alone ("2026-08-16") is already parsed as UTC midnight by the spec, and is a
  // calendar date rather than an instant — it must not be shifted.
  const isDateTime = trimmed.includes('T');
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);

  const date = new Date(isDateTime && !hasZone ? `${trimmed}Z` : trimmed);

  return Number.isNaN(date.getTime()) ? null : date;
}
